/**
 * BeatCard — the CK2-event view of one mission's current beat (#885).
 *
 * Framing prose (the node's authored flavor text), then the LIVE options
 * as succinct buttons (the server already applied location ∧ visibility —
 * an empty list means "nothing you can do HERE; follow the compass").
 * Clicking resolves: the roll/result prose renders in place (it also
 * lands in the scroll as a STORY narrative block), then Continue moves
 * to the next beat or the epilogue.
 *
 * A rolled option carries a Call on favor control (#4333): the chosen being's invocation
 * rolls first and its bonus joins the option's roll; the result shows the invocation's two
 * lines and the check's breakdown with the favor line.
 */
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { CallOnFavor } from '@/worship/components/CallOnFavor';
import type { InvocationChoice } from '@/worship/types';

import { ApiValidationError, flattenErrorMessage } from '../api';
import { useBeat, useResolveBeat } from '../queries';
import type { BeatOption, ResolvedBeat } from '../types';
import { BeatTrack } from './BeatTrack';
import { InvocationResult } from './InvocationResult';

function optionKey(option: BeatOption): string {
  return `${option.option_id}-${option.approach_id ?? 'authored'}`;
}

interface BeatCardProps {
  instanceId: number;
  /** Stable identifier of the player's current room (refetch key). */
  roomKey: string;
}

export function BeatCard({ instanceId, roomKey }: BeatCardProps) {
  const { data: beat, isLoading } = useBeat(instanceId, roomKey);
  const resolve = useResolveBeat();
  const [result, setResult] = useState<ResolvedBeat | null>(null);
  const [invocations, setInvocations] = useState<Record<string, InvocationChoice | null>>({});

  if (result) {
    return (
      <div className="space-y-2 rounded border bg-card p-3" data-testid="beat-result">
        {result.invocation ? <InvocationResult invocation={result.invocation} /> : null}
        {result.outcome_name ? <Badge variant="outline">{result.outcome_name}</Badge> : null}
        <p className="whitespace-pre-wrap text-sm">{result.story_text}</p>
        {result.is_terminal ? (
          <p className="whitespace-pre-wrap text-sm italic text-muted-foreground">
            {result.epilogue || 'The story concludes.'}
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setResult(null)}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-3 text-sm text-muted-foreground">…</div>;
  }
  if (!beat) {
    return (
      <div className="p-3 text-sm text-muted-foreground" data-testid="beat-concluded">
        This story has concluded: see your journal for how it ended.
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded border bg-card p-3" data-testid="beat-card">
      {beat.flavor_text ? <p className="whitespace-pre-wrap text-sm">{beat.flavor_text}</p> : null}
      <BeatTrack track={beat.track} />
      {beat.options.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="beat-not-here">
          Nothing presents itself here: this story waits somewhere else.
        </p>
      ) : (
        <div className="space-y-1" data-testid="beat-options">
          {beat.options.map((option) => {
            const key = optionKey(option);
            const invocation = invocations[key] ?? null;
            return (
              <div key={key} className="space-y-1" data-testid="beat-option">
                <OptionButton
                  option={option}
                  pending={resolve.isPending}
                  onPick={() =>
                    resolve.mutate(
                      {
                        instanceId,
                        option_id: option.option_id,
                        approach_id: option.approach_id,
                        ...(invocation
                          ? { invoke_being_id: invocation.beingId, invoke_line: invocation.line }
                          : {}),
                      },
                      {
                        onSuccess: (resolved) => {
                          setInvocations({});
                          setResult(resolved);
                        },
                      }
                    )
                  }
                />
                {option.rolls_check ? (
                  <div className="flex justify-end">
                    <CallOnFavor
                      value={invocation}
                      onChange={(choice) => setInvocations((prev) => ({ ...prev, [key]: choice }))}
                      disabled={resolve.isPending}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {resolve.error ? (
        <p className="text-xs text-destructive" data-testid="beat-error">
          {resolve.error instanceof ApiValidationError
            ? flattenErrorMessage(resolve.error.fieldErrors)
            : resolve.error.message}
        </p>
      ) : null}
    </div>
  );
}

/** The option's label with its "because ..." lines (why this character qualifies). */
export function OptionLabel({ option }: { option: BeatOption }) {
  return (
    <span>
      {option.label}
      {option.reasons.map((reason) => (
        <span
          key={reason}
          className="block text-xs italic text-muted-foreground"
          data-testid="option-reason"
        >
          because {reason}
        </span>
      ))}
    </span>
  );
}

function OptionButton({
  option,
  pending,
  onPick,
}: {
  option: BeatOption;
  pending: boolean;
  onPick: () => void;
}) {
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-auto w-full justify-between whitespace-normal py-1.5 text-left"
      disabled={pending}
      onClick={onPick}
    >
      <OptionLabel option={option} />
      {option.check_type_name ? (
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
          {option.check_type_name}
          {option.base_risk > 0 ? ` · risk ${option.base_risk}` : ''}
        </span>
      ) : null}
    </Button>
  );
}
